import React, { useMemo } from "react";

import ListGroup, { ListGroupItem } from "./ListGroup";
import { LabelMode } from "../ui_atoms/Label";
import { useActivityNavigationInfo } from "../hooks/activities";
import { IconName } from "./Icon";
import { SHORT_DATE_IN_LIST_FORMAT_OPTIONS, useDateFormatter } from "../hooks/date";

export enum ActivityType {
    todo = "todo",
    log = "log",
}

export type Activity = {
    id: string;
    name: string;
    activityKey?: string | null;
    createdAt: string;
    title: string;
    text: string;
    badgeUrl?: string | null;
    actionText?: string | null;
    at: Date;
    priority: number;
    type: ActivityType;
    isActive?: boolean;
    sourceName?: string | null;
};

type ActivityListViewProps = {
    activities: Activity[];
    onSelectActivity?: (activity: Activity) => void | Promise<void>;
    disabled?: boolean;
    shadowActivityId?: string;
};

const basquiatAvatarUrl = require("../assets/images/basquiat-avatar.png");

export type UseActivityListItemsOptions = {
    onSelectActivity?: (activity: Activity) => void | Promise<void>;
    disabled?: boolean;
    shadowActivityId?: string;
};

/**
 * Maps activities to `ListGroupItem`s. Extracted so screens can virtualize the list at the
 * ScreenCard level (`virtualizedItems`/`renderItem` + `useListGroupRenderItem`) while
 * non-virtualized callers keep using `ActivityListView` below.
 */
export const useActivityListItems = (
    activities: Activity[],
    { onSelectActivity, disabled = false, shadowActivityId }: UseActivityListItemsOptions = {},
): ListGroupItem<string>[] => {
    const getActivityNavigationInfo = useActivityNavigationInfo();
    const intDateFormatter = useDateFormatter();

    return useMemo(
        () =>
            activities.map((activity) => ({
                title: activity.title,
                description: activity.text,
                value: activity.id,
                unread: activity.isActive,
                labels: [{ text: activity.actionText || undefined, mode: "cta" as LabelMode }],
                tags: activity.sourceName
                    ? [
                          intDateFormatter(activity.at, SHORT_DATE_IN_LIST_FORMAT_OPTIONS),
                          activity.sourceName,
                      ]
                    : [intDateFormatter(activity.at, SHORT_DATE_IN_LIST_FORMAT_OPTIONS)],
                avatarPath: activity.badgeUrl || basquiatAvatarUrl,
                useBigAvatarOrIcon: true,
                onPress: disabled
                    ? undefined
                    : () => {
                          void onSelectActivity?.(activity);
                      },
                isShaded: shadowActivityId === activity.id,
                rightIcon: getActivityNavigationInfo(activity.name, activity.activityKey)
                    ? "chevron-right"
                    : ("__empty__" as IconName),
            })),
        [
            activities,
            intDateFormatter,
            disabled,
            shadowActivityId,
            onSelectActivity,
            getActivityNavigationInfo,
        ],
    );
};

export const ActivityListView: React.FC<ActivityListViewProps> = ({
    activities,
    onSelectActivity,
    disabled = false,
    shadowActivityId,
}) => {
    const items = useActivityListItems(activities, {
        onSelectActivity,
        disabled,
        shadowActivityId,
    });
    return <ListGroup isItemsCheckable={false} items={items} alignment="stretch" />;
};

export default ActivityListView;
